import type { FetchResult, MutationFunctionOptions } from '@apollo/client';
import type { ResultOf, VariablesOf } from '@graphql-typed-document-node/core';
import type { PROMOTE_ADVISOR_ONBOARDING_ENROLLMENT_MUTATION } from 'src/features/advisor-book-onboarding/api/advisor-book-onboarding-gql';
import { errAsync, okAsync, ResultAsync } from 'neverthrow';
import { getPromoteAdvisorOnboardingEnrollmentResult } from 'src/features/advisor-book-onboarding/api/advisor-book-onboarding-gql';

type PromoteEnrollmentMutationData = ResultOf<typeof PROMOTE_ADVISOR_ONBOARDING_ENROLLMENT_MUTATION>;
type PromoteEnrollmentMutationVariables = VariablesOf<typeof PROMOTE_ADVISOR_ONBOARDING_ENROLLMENT_MUTATION>;

export type PromoteEnrollmentMutation = (
  options: MutationFunctionOptions<PromoteEnrollmentMutationData, PromoteEnrollmentMutationVariables>,
) => Promise<FetchResult<PromoteEnrollmentMutationData>>;

export type PromoteEnrollmentSuccess = {
  readonly kind: 'success';
  readonly promotedCount: number;
  readonly blockedCount: number;
  readonly skippedCount: number;
};

export type PromoteEnrollmentFailure = {
  readonly kind: 'failure';
  readonly message: string;
  readonly cause?: unknown;
};

type CallPromoteAdvisorOnboardingEnrollmentParams = {
  readonly promoteEnrollment: PromoteEnrollmentMutation;
  readonly enrollmentUuid: string;
  readonly fallbackMessage: string;
};

export function callPromoteAdvisorOnboardingEnrollment({
  promoteEnrollment,
  enrollmentUuid,
  fallbackMessage,
}: CallPromoteAdvisorOnboardingEnrollmentParams): ResultAsync<PromoteEnrollmentSuccess, PromoteEnrollmentFailure> {
  return ResultAsync.fromPromise(
    promoteEnrollment({
      variables: {
        input: {
          uuid: enrollmentUuid,
        },
      },
    }),
    (cause): PromoteEnrollmentFailure => ({
      kind: 'failure',
      message: fallbackMessage,
      cause,
    }),
  ).andThen((result) => {
    const payload = getPromoteAdvisorOnboardingEnrollmentResult(result.data);
    if (payload === undefined) {
      return errAsync({
        kind: 'failure' as const,
        message: fallbackMessage,
      });
    }

    if (!payload.success) {
      const firstError = payload.errors?.[0]?.message;
      return errAsync({
        kind: 'failure' as const,
        message: firstError ?? fallbackMessage,
      });
    }

    return okAsync({
      kind: 'success' as const,
      promotedCount: payload.promotedCount,
      blockedCount: payload.blockedCount,
      skippedCount: payload.skippedCount,
    });
  });
}
